import { BaseItemsParamsDto } from './../../models/dto/Base-Test-item-dto';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { TestCasesActions } from './testCases.actions';
import { Scope } from 'src/app/services/migration/dual-api.service';
import { TestCasesStateModel } from './testCases.state.model';
import { patch, removeItem } from '@ngxs/store/operators';
import { TestCaseApiService } from 'src/app/services/api/test-case-api.service';
import { BaseTestItemDto } from 'src/app/models/dto/Base-Test-item-dto';
import { StoredItems } from 'src/app/models/storedItems';
import { firstValueFrom } from 'rxjs';
import { tap } from 'rxjs/operators';
import { TestCaseDto } from 'src/app/models/dto/testCaseDto';

@State<TestCasesStateModel>({
  name: 'testCases',
  defaults: {
    testCases: { totalCount: 0, pageSize: 0, pageCache:  new Map<number, BaseTestItemDto[]>(), items: []} as StoredItems,
    functionalGroupTestCases: { totalCount: 0, pageSize: 0, pageCache:  new Map<number, BaseTestItemDto[]>(), items: []} as StoredItems,
    myTestCases:  { totalCount: 0, pageSize: 0, pageCache:  new Map<number, BaseTestItemDto[]>(), items: []} as StoredItems,
    searchedTestCases:  { totalCount: 0, pageSize: 0, pageCache:  new Map<number, BaseTestItemDto[]>(), items: []} as StoredItems,
  },
})

@Injectable()
export class TestCasesState {
  constructor(private testCaseApiService: TestCaseApiService) {}

 @Action(TestCasesActions.LoadPageOfAllTestCases)
  async LoadPageOfAllTestCases(ctx: StateContext<TestCasesStateModel>, action: TestCasesActions.LoadPageOfAllTestCases) {
    const params: BaseItemsParamsDto = {
      pageSize: action.pageSize,
      currentPage: action.page
    };

    await this.loadStoredItemsPage(ctx, "testCases", params);

    // Trigger prefetch for next page
    const stored = ctx.getState().testCases;
    if (!stored.pageCache.has(action.page + 1)) {
      ctx.dispatch(new TestCasesActions.PrefetchNextPageOfAllTestCases(action.page + 1, action.pageSize));
    }
  }

  @Action(TestCasesActions.PrefetchNextPageOfAllTestCases)
  async PrefetchNextPageOfAllTestCases(ctx: StateContext<TestCasesStateModel>, action: TestCasesActions.PrefetchNextPageOfAllTestCases) {
    const params: BaseItemsParamsDto = {
      pageSize: action.pageSize,
      currentPage: action.page
    };

    const stored = ctx.getState().testCases;
    if (stored.pageCache.has(action.page)) return;

    await this.loadAndStoreStoredItemsPage(ctx, "testCases", params, false);
  }

  @Action(TestCasesActions.LoadPageOfFunctionalGroupTestCasesPaginated)
  async LoadPageOfFunctionalGroupTestCasesPaginated(ctx: StateContext<TestCasesStateModel>, action: TestCasesActions.LoadPageOfFunctionalGroupTestCasesPaginated) {
    const params: BaseItemsParamsDto = {
      pageSize: action.pageSize,
      currentPage: action.page,
      orgGroupId: action.groupId
    };

    await this.loadStoredItemsPage(ctx, "functionalGroupTestCases", params);

    // Trigger prefetch for next page
    const stored = ctx.getState().functionalGroupTestCases;
    if (!stored.pageCache.has(action.page + 1)) {
      ctx.dispatch(new TestCasesActions.PrefetchNextPageOfFunctionalGroupTestCases(action.page + 1, action.pageSize, action.groupId));
    }
  }

  @Action(TestCasesActions.PrefetchNextPageOfFunctionalGroupTestCases)
  async PrefetchNextPageOfFunctionalGroupTestCases(ctx: StateContext<TestCasesStateModel>, action: TestCasesActions.PrefetchNextPageOfFunctionalGroupTestCases) {
    const params: BaseItemsParamsDto = {
      pageSize: action.pageSize,
      currentPage: action.page,
      orgGroupId: action.groupId
    };

    const stored = ctx.getState().functionalGroupTestCases;
    if (stored.pageCache.has(action.page)) return;

    await this.loadAndStoreStoredItemsPage(ctx, "functionalGroupTestCases", params, false);
  }

  @Action(TestCasesActions.LoadPageOfMyTestCases)
  async SetMyTestCases(ctx: StateContext<TestCasesStateModel>, action: TestCasesActions.LoadPageOfMyTestCases) {
    const params: BaseItemsParamsDto = {
      pageSize: action.pageSize,
      currentPage: action.page,
      regBy: action.userId
    };

    await this.loadStoredItemsPage(ctx, "myTestCases", params);

    // Trigger prefetch for next page
    const stored = ctx.getState().myTestCases;
    if (!stored.pageCache.has(action.page + 1)) {
      ctx.dispatch(new TestCasesActions.PrefetchNextPageOfMyTestCases(action.page + 1, action.pageSize, action.userId));
    }
  }

  @Action(TestCasesActions.PrefetchNextPageOfMyTestCases)
  async PrefetchNextPageOfMyTestCases(ctx: StateContext<TestCasesStateModel>, action: TestCasesActions.PrefetchNextPageOfMyTestCases) {
    const params: BaseItemsParamsDto = {
      pageSize: action.pageSize,
      currentPage: action.page,
      regBy: action.userId
    };

    const stored = ctx.getState().myTestCases;
    if (stored.pageCache.has(action.page)) return;

    await this.loadAndStoreStoredItemsPage(ctx, "myTestCases", params, false);
  }

  @Action(TestCasesActions.LoadPageOfSearchedTestCases)
  async SetSearchedTestCases(ctx: StateContext<TestCasesStateModel>, action: TestCasesActions.LoadPageOfSearchedTestCases) {
    const params: BaseItemsParamsDto = {
      pageSize: action.pageSize,
      currentPage: action.page,
    };

    await this.loadStoredItemsPageForSearch(ctx, action.testCase, action.resetSearch, params, action.scope);
  }

  private async loadStoredItemsPageForSearch(ctx: StateContext<TestCasesStateModel>, searchItem: TestCaseDto, resetSearch: boolean, apiParams: BaseItemsParamsDto, scope?: Scope) {
    let stateProperty = "searchedTestCases" as keyof TestCasesStateModel;
    const state = ctx.getState();
    const stored = state[stateProperty] as StoredItems;

    if(!resetSearch) {
      let success = this.tryLoadFromCache(ctx, stateProperty, apiParams);
      if(!success) {
        ctx.patchState({
          [stateProperty]: {
            ...stored,
            items: []
          }
        });
        console.log("error with searched cached items");
      }
      return;
    }

    this.resetCache(ctx, stateProperty, apiParams.pageSize);

    const allItems = await firstValueFrom(this.testCaseApiService.search(searchItem, scope)) ?? [];
    const sortedItems = [...allItems].sort((a, b) => {
      const aTime = a?.modificationTime ? new Date(a.modificationTime).getTime() : 0;
      const bTime = b?.modificationTime ? new Date(b.modificationTime).getTime() : 0;
      return bTime - aTime;
    });
    const pageCount = Math.ceil(sortedItems.length / apiParams.pageSize);
    const refreshedStored = ctx.getState()[stateProperty] as StoredItems;
    const newCache = new Map<number, BaseTestItemDto[]>();

    // Fill cache for each page
    for (let page = 1; page <= pageCount; page++) {
      const start = (page - 1) * apiParams.pageSize;
      const end = start + apiParams.pageSize;

      const pageItems = sortedItems.slice(start, end);
      newCache.set(page, pageItems);
    }

    ctx.patchState({
      [stateProperty]: {
        ...refreshedStored,
        items: newCache.get(apiParams.currentPage) ?? [],
        totalCount: sortedItems.length,
        pageCache: newCache
      }
    });
  }

  //todo add check for update needed
   private async loadStoredItemsPage(ctx: StateContext<TestCasesStateModel>, stateProperty: keyof TestCasesStateModel, apiParams: BaseItemsParamsDto) {
    this.resetCacheIfStale(ctx, stateProperty, apiParams.pageSize);

    if (!this.tryLoadFromCache(ctx, stateProperty, apiParams))
      await this.loadAndStoreStoredItemsPage(ctx, stateProperty, apiParams, true);
  }

  private resetCacheIfStale(ctx: StateContext<TestCasesStateModel>, stateProperty: keyof TestCasesStateModel, pageSize: number) {
    const state = ctx.getState();
    const stored = state[stateProperty] as StoredItems;

    //if page size is not changed => don't reset cache
    if (stored.pageSize === pageSize)
      return;

    this.resetCache(ctx, stateProperty, pageSize);
  }

    private resetCache(ctx: StateContext<TestCasesStateModel>, stateProperty: keyof TestCasesStateModel, pageSize: number) {
    const state = ctx.getState();
    const stored = state[stateProperty] as StoredItems;

    ctx.patchState({
      [stateProperty]: {
        ...stored,
        pageSize,
        pageCache: new Map<number, BaseTestItemDto[]>(),
        items: []
      }
    });
  }

  private tryLoadFromCache(ctx: StateContext<TestCasesStateModel>, stateProperty: keyof TestCasesStateModel, apiParams: BaseItemsParamsDto) {
    const state = (ctx.getState()[stateProperty] as StoredItems);

    if (!state.pageCache.has(apiParams.currentPage))
      return false;

    ctx.patchState({
      [stateProperty]: {
        ...state,
        items: [...state.pageCache.get(apiParams.currentPage)!]
      }
    });

    return true;
  }

  private async loadAndStoreStoredItemsPage(ctx: StateContext<TestCasesStateModel>, stateProperty: keyof TestCasesStateModel, apiParams: BaseItemsParamsDto, updateUI: boolean) {
    const stored = ctx.getState()[stateProperty] as StoredItems;

    const response = await firstValueFrom(this.testCaseApiService.getAllPaginatedPages(apiParams));
    if (!response) return;

    const newCache = new Map(stored.pageCache);
    newCache.set(apiParams.currentPage, [...response.items]);

    if (updateUI) {
      ctx.patchState({
        [stateProperty]: {
          ...stored,
          items: [...response.items],
          totalCount: response.totalCount,
          pageCache: newCache
        }
      });
    } else {
      ctx.patchState({
        [stateProperty]: {
          ...stored,
          pageCache: newCache
        }
      });
    }
  }

  @Action(TestCasesActions.DeleteTestCase)
  DeleteTestCase(ctx: StateContext<TestCasesStateModel>, action: TestCasesActions.DeleteTestCase){
    return this.testCaseApiService.delete(action.testCaseUid, action.regBy).pipe(
      tap(result =>{
        ctx.setState(
          patch<TestCasesStateModel>({
            functionalGroupTestCases: patch({
              items: removeItem<BaseTestItemDto>(x => x.uid === action.testCaseUid),
              totalCount: ctx.getState().testCases.totalCount - 1
            }),
             testCases: patch({
              items: removeItem<BaseTestItemDto>(x => x.uid === action.testCaseUid),
              totalCount: ctx.getState().testCases.totalCount - 1
            })
          })
          //todo remove from by my tc as well
          //add check if deleted is by me or by my group
        )
      })
    );
  }

  @Action(TestCasesActions.UpdateItem)
  updateItem(ctx: StateContext<TestCasesStateModel>, action: TestCasesActions.UpdateItem) {
    const state = ctx.getState();

    // Helper to update both items array and pageCache for a given StoredItems
    const updateStoredItems = (stored: StoredItems): StoredItems | null => {
      let changed = false;

      // Update items array
      const itemIdx = stored.items.findIndex(item => item.uid === action.item.uid);
      const updatedItems = itemIdx >= 0
        ? stored.items.map((item, idx) => idx === itemIdx ? action.item : item)
        : stored.items;
      if (itemIdx >= 0) changed = true;

      // Update pageCache - iterate all cached pages and replace matching items
      const updatedCache = new Map<number, BaseTestItemDto[]>();
      for (const [pageNum, pageItems] of stored.pageCache.entries()) {
        const pageIdx = pageItems.findIndex(item => item.uid === action.item.uid);
        if (pageIdx >= 0) {
          updatedCache.set(pageNum, pageItems.map((item, idx) => idx === pageIdx ? action.item : item));
          changed = true;
        } else {
          updatedCache.set(pageNum, pageItems);
        }
      }

      return changed ? { ...stored, items: updatedItems, pageCache: updatedCache } : null;
    };

    // Update all state properties
    const updatedTestCases = updateStoredItems(state.testCases);
    const updatedFgTestCases = updateStoredItems(state.functionalGroupTestCases);
    const updatedMyTestCases = updateStoredItems(state.myTestCases);
    const updatedSearchedTestCases = updateStoredItems(state.searchedTestCases);

    // Only patch if at least one changed (helper returns null if unchanged)
    if (updatedTestCases || updatedFgTestCases || updatedMyTestCases || updatedSearchedTestCases) {
      ctx.patchState({
        testCases: updatedTestCases ?? state.testCases,
        functionalGroupTestCases: updatedFgTestCases ?? state.functionalGroupTestCases,
        myTestCases: updatedMyTestCases ?? state.myTestCases,
        searchedTestCases: updatedSearchedTestCases ?? state.searchedTestCases
      });
    }
  }

  @Action(TestCasesActions.ClearCache)
  clearCache(ctx: StateContext<TestCasesStateModel>) {
    ctx.patchState({
      testCases: { totalCount: 0, pageSize: 0, pageCache:  new Map<number, BaseTestItemDto[]>(), items: []} as StoredItems,
      functionalGroupTestCases: { totalCount: 0, pageSize: 0, pageCache:  new Map<number, BaseTestItemDto[]>(), items: []} as StoredItems,
      myTestCases: { totalCount: 0, pageSize: 0, pageCache:  new Map<number, BaseTestItemDto[]>(), items: []} as StoredItems,
      searchedTestCases: { totalCount: 0, pageSize: 0, pageCache:  new Map<number, BaseTestItemDto[]>(), items: []} as StoredItems,
    });
  }

  @Selector()
  static getAllTestCases(state: TestCasesStateModel) {
    return state.testCases.items;
  }

  @Selector()
  static getFunctionalGroupTestCases(state: TestCasesStateModel) {
    return state.functionalGroupTestCases.items;
  }

  @Selector()
  static getMyTestCases(state: TestCasesStateModel) {
    return state.myTestCases.items;
  }

  @Selector()
  static getSearchedTestCases(state: TestCasesStateModel) {
    return state.searchedTestCases.items;
  }

  // Search results are fetched in full and sliced client-side into pageCache (see
  // loadStoredItemsPageForSearch), so flattening the cache gives every matching item
  // without another request — used for "select all filtered items".
  @Selector()
  static getSearchedTestCasesFull(state: TestCasesStateModel): BaseTestItemDto[] {
    return Array.from(state.searchedTestCases.pageCache.values()).flat();
  }

  @Selector()
  static getTotalCountForAll(state: TestCasesStateModel): number {
    return state.testCases.totalCount;
  }

  @Selector()
  static getTotalCountByGroup(state: TestCasesStateModel): number {
    return state.functionalGroupTestCases.totalCount;
  }

  @Selector()
  static getTotalCountByUser(state: TestCasesStateModel): number {
      return state.myTestCases.totalCount;
    }

  @Selector()
  static getTotalCountSearched(state: TestCasesStateModel): number {
    return state.searchedTestCases.totalCount;
  }
}
